import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, MapPin, Stethoscope, Building2, Loader2, ShieldCheck, ChevronDown } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors";

const InterestForm = ({ vacancy, onDone }) => {
  const [form, setForm] = useState({ name: "", email: "", credentials: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ref, setRef] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`/vacancies/${vacancy.id}/interest`, form);
      setRef(data.reference_id);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (ref)
    return (
      <div className="mt-4 p-4 rounded-xl bg-[#0B1B2B] text-center" data-testid={`interest-confirmation-${vacancy.id}`}>
        <ShieldCheck size={20} className="text-[#D4AF37] mx-auto" />
        <p className="mt-2 text-white text-sm font-semibold">Interest Received</p>
        <p className="mt-1 text-slate-400 text-xs">Reference <span className="text-[#D4AF37] font-semibold">{ref}</span> · Response within 1–2 business days</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="mt-4 space-y-3" data-testid={`interest-form-${vacancy.id}`}>
      <input className={inputCls} placeholder="Full Name" required value={form.name} onChange={set("name")} data-testid={`interest-name-${vacancy.id}`} />
      <input className={inputCls} type="email" placeholder="Email" required pattern="[^@\s]+@[^@\s]+\.[^@\s]{2,}" value={form.email} onChange={set("email")} data-testid={`interest-email-${vacancy.id}`} />
      <input className={inputCls} placeholder="Credentials (e.g., FNP-C, MSN-Ed)" value={form.credentials} onChange={set("credentials")} data-testid={`interest-credentials-${vacancy.id}`} />
      <textarea className={`${inputCls} min-h-[70px]`} placeholder="Brief note (optional)" value={form.message} onChange={set("message")} data-testid={`interest-message-${vacancy.id}`} />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button type="submit" disabled={busy} className="w-full bg-[#D4AF37] text-[#0B1B2B] text-sm font-semibold py-2.5 rounded-xl hover:bg-[#e8c968] transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid={`interest-submit-${vacancy.id}`}>
        {busy && <Loader2 size={14} className="animate-spin" />}
        Submit Interest
      </button>
    </form>
  );
};

export const VacancyBoard = () => {
  const [vacancies, setVacancies] = useState([]);
  const [openForm, setOpenForm] = useState(null);

  useEffect(() => {
    api.get("/vacancies").then(({ data }) => setVacancies(data)).catch(() => {});
  }, []);

  if (vacancies.length === 0) return null;

  return (
    <section id="vacancies" className="py-20 md:py-32 bg-white" data-testid="vacancy-board-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Clinical Placement Board</p>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl text-[#0B1B2B] tracking-tight leading-tight">
            Open Pod Placements & Faculty Engagements.
          </h2>
          <p className="mt-5 text-[#64748B] leading-relaxed text-sm md:text-base">
            Live openings across our clinical pod network — faculty preceptorships, supervisory roles, and shared
            clinical faculty engagements. Express interest and our operations team will respond within 1–2 business days.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {vacancies.map((v, idx) => (
            <motion.div
              key={v.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (idx % 2) * 0.1 }}
              className={`border rounded-xl p-6 transition-all duration-300 ${v.status === "open" ? "bg-[#F8FAFC] border-[#E2E8F0] hover:border-[#D4AF37]/60 hover:shadow-lg" : "bg-white border-[#E2E8F0] opacity-60"}`}
              data-testid={`vacancy-card-${v.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-serif text-lg text-[#0B1B2B] font-semibold leading-snug">{v.title}</h3>
                <span className={`shrink-0 text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full ${v.status === "open" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`} data-testid={`vacancy-status-${v.id}`}>
                  {v.status}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-[#64748B]">
                <span className="flex items-center gap-1.5"><Stethoscope size={13} className="text-[#D4AF37]" /> {v.specialty}</span>
                <span className="flex items-center gap-1.5"><Building2 size={13} className="text-[#D4AF37]" /> {v.setting}</span>
                <span className="flex items-center gap-1.5"><MapPin size={13} className="text-[#D4AF37]" /> {v.location}</span>
                <span className="flex items-center gap-1.5"><Briefcase size={13} className="text-[#D4AF37]" /> {v.engagement}</span>
              </div>
              {v.status === "open" && (
                <>
                  <button
                    onClick={() => setOpenForm(openForm === v.id ? null : v.id)}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#0B1B2B] px-4 py-2.5 rounded-xl border border-[#D4AF37]/60 hover:bg-[#D4AF37]/10 transition-colors"
                    data-testid={`express-interest-${v.id}`}
                  >
                    Express Interest
                    <ChevronDown size={14} className={`transition-transform duration-300 ${openForm === v.id ? "rotate-180" : ""}`} />
                  </button>
                  {openForm === v.id && <InterestForm vacancy={v} />}
                </>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
